<template>
    <div class="main-content">
        <InfoPopup v-model="showInfoPopup" :title="infoTitle" :showCancelButton="showInfoPopupCancelButton">
            <p>{{ infoMessage }}</p>
        </InfoPopup>
        <div class="credit-info">
            <h3>Credit Management</h3>
            <div class="balance-row">
                <div class="balance-card">
                    <div class="balance-label">Credit Balance</div>
                    <div class="balance">{{ loadingBalance ? '' : balance.toLocaleString() }}</div>
                </div>
            </div>
        </div>
        <div class="credit-payment-panel-outer">
            <h3>Add Credits</h3>
            <div class="credit-payment-panel">
                <div class="credit-grid">
                    <div v-for="option in creditOptions" :key="option.id" class="credit-option"
                        :class="{ selected: selectedOption && selectedOption.id === option.id }"
                        @click="selectOption(option)">
                        <div class="credit-amount">{{ option.label }}</div>
                        <div class="credit-price">${{ option.price }}</div>
                    </div>
                </div>
                <div class="payment-panel" :class="{ 'payment-panel-empty': !selectedOption }">
                    <p class="payment-panel-text" :class="{ highlight: highlightMessage }" v-if="!selectedOption">
                        Please pick a package
                        <span class="arrow-hint">&#8592;</span>
                    </p>
                    <p class="payment-panel-text" v-else>
                        You have selected:
                        <span>{{ selectedOption.label }} Credits</span>
                    </p>
                    <button @click="startCheckout" :disabled="!selectedOption" class="checkout-btn">
                        Proceed to Payment
                    </button>
                </div>
            </div>
        </div>
        <div class="credit-history">
            <h3>Credit Transaction History</h3>
            <div class="filter-panel" v-if="balanceHistory && balanceHistory.length">
                <label>
                    Type:
                    <select v-model="selectedType">
                        <option value="">All</option>
                        <option v-for="type in uniqueTypes" :key="type" :value="type">{{ type }}</option>
                    </select>
                </label>
                <label>
                    Description:
                    <select v-model="selectedDescription">
                        <option value="">All</option>
                        <option v-for="desc in uniqueDescriptions" :key="desc" :value="desc">{{ desc }}</option>
                    </select>
                </label>
                <label>
                    Date:
                    <select v-model="selectedDateRange">
                        <option value="">All</option>
                        <option value="today">Today</option>
                        <option value="last_7_days">Last 7 Days</option>
                        <option value="last_30_days">Last 30 Days</option>
                        <option value="last_365_days">Last 365 Days</option>
                    </select>
                </label>
                <label>
                    Rows:
                    <select v-model.number="pageSize">
                        <option v-for="size in pageSizeOptions" :key="size" :value="size">{{ size }}</option>
                    </select>
                </label>
            </div>
            <table class="history-table" v-if="balanceHistory && balanceHistory.length">
                <thead>
                    <tr>
                        <th>Date</th>
                        <th>Description</th>
                        <th>Amount</th>
                        <th>Balance After</th>
                        <th>Type</th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="tx in paginatedHistory" :key="tx.id">
                        <td>{{ new Date(tx.created_at_time * 1000).toLocaleString() }}</td>
                        <td>{{ detailTypeMap[tx.detail_type] || tx.detail_type }}</td>
                        <td :class="['amt', tx.trx_type]">{{ tx.amount.toLocaleString() }}</td>
                        <td>{{ tx.balance_after.toLocaleString() }}</td>
                        <td :class="['type', tx.trx_type]">{{ tx.trx_type }}</td>
                    </tr>
                </tbody>
            </table>
            <div class="pagination" v-if="totalPages > 1">
                <button @click="prevPage" :disabled="currentPage === 1">Prev</button>
                <span>Page {{ currentPage }} / {{ totalPages }}</span>
                <button @click="nextPage" :disabled="currentPage === totalPages">Next</button>
            </div>
            <p v-if="filteredHistory.length === 0" class="no-trx">No transactions yet.</p>
        </div>
    </div>
</template>
<script>
import { ref, computed, watch } from 'vue'
import { useRouter, useRoute } from 'vue-router';
import { requireAuth } from '@/api/auth';
import { getCreditTransactions, postCreditPay, getCreditBalance } from '@/api/credits';
import InfoPopup from '@/components/common/InfoPopup.vue';
export default {
    name: 'CreditManagement',
    components: { InfoPopup },
    setup() {
        const route = useRoute()
        const status = ref(route.query.status || '')
        watch(
            () => route.query.status,
            val => { status.value = val || '' }
        )
        const router = useRouter();
        const showInfoPopup = ref(false);
        const infoTitle = ref('');
        const infoMessage = ref('');
        const showInfoPopupCancelButton = ref(true);

        watch(status, val => {
            if (val === 'success') {
                infoTitle.value = 'Payment Successful!';
                infoMessage.value = '🎉 Payment successful! Your credits are updating now.';
                showInfoPopup.value = true;
                showInfoPopupCancelButton.value = false;
            } else if (val === 'cancel') {
                infoTitle.value = 'Payment Canceled.';
                infoMessage.value = '⚠️ Payment was canceled. No changes were made.';
                showInfoPopup.value = true;
                showInfoPopupCancelButton.value = false;
            }
            if (val === 'success' || val === 'cancel') {
                router.replace({
                    query: { ...route.query, status: undefined }
                });
            }
        }, { immediate: true });

        const pageSizeOptions = [10, 20, 50];
        const pageSize = ref(10);
        const currentPage = ref(1);

        const creditPriceId = (key) => process.env[`VUE_APP_CREDIT_${key}_PRICE_ID`] || '';
        const creditOptions = ref([
            { id: 1, label: '100K', price: 10, priceId: creditPriceId('100K') },
            { id: 2, label: '200K', price: 20, priceId: creditPriceId('200K') },
            { id: 3, label: '300K', price: 30, priceId: creditPriceId('300K') },
            { id: 4, label: '400K', price: 40, priceId: creditPriceId('400K') },
            { id: 5, label: '600K', price: 60, priceId: creditPriceId('600K') },
            { id: 6, label: '800K', price: 80, priceId: creditPriceId('800K') }
        ]);
        const detailTypeMap = {
            add_subscription_refill: "Subscription Refill",
            add_credit_pack: "Credit Pack Purchase",
            consume_tts: "TTS Service Usage",
            consume_voice_select: "Voice Selection Usage",
            consume_voice_design: "Voice Design Usage",
            consume_voice_clone: "Voice Cloning Usage",
            add_new_user: "Welcome Credits"
        };
        const balance = ref(1);
        const loadingBalance = ref(true);
        const balanceHistory = ref([]);

        const sortedHistory = computed(() => {
            return [...balanceHistory.value].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
        });

        const selectedOption = ref(null);
        const highlightMessage = ref(false);

        const selectedType = ref('');
        const selectedDescription = ref('');
        const selectedDateRange = ref('');

        const uniqueTypes = computed(() => {
            const types = new Set(balanceHistory.value.map(tx => tx.trx_type));
            return [...types];
        });

        const uniqueDescriptions = computed(() => {
            const descs = new Set(balanceHistory.value.map(tx => detailTypeMap[tx.detail_type] || tx.detail_type));
            return [...descs];
        });

        const filteredHistory = computed(() => {
            const now = new Date();
            return sortedHistory.value.filter(tx => {
                const matchType = selectedType.value === '' || tx.trx_type === selectedType.value;
                const description = detailTypeMap[tx.detail_type] || tx.detail_type;
                const matchDescription = selectedDescription.value === '' || description === selectedDescription.value;
                const createdAt = new Date(tx.created_at_time * 1000);
                let matchDate = true;

                if (selectedDateRange.value === 'today') {
                    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
                    matchDate = createdAt >= today;
                } else if (selectedDateRange.value === 'last_7_days') {
                    const daysAgo = new Date();
                    daysAgo.setDate(now.getDate() - 7);
                    matchDate = createdAt >= daysAgo;
                } else if (selectedDateRange.value === 'last_30_days') {
                    const daysAgo = new Date();
                    daysAgo.setDate(now.getDate() - 30);
                    matchDate = createdAt >= daysAgo;
                } else if (selectedDateRange.value === 'last_365_days') {
                    const daysAgo = new Date();
                    daysAgo.setDate(now.getDate() - 365);
                    matchDate = createdAt >= daysAgo;
                }

                return matchType && matchDescription && matchDate;
            });
        });

        const totalPages = computed(() => {
            return Math.max(1, Math.ceil(filteredHistory.value.length / pageSize.value));
        });

        const paginatedHistory = computed(() => {
            const start = (currentPage.value - 1) * pageSize.value;
            return filteredHistory.value.slice(start, start + pageSize.value);
        });

        function nextPage() {
            if (currentPage.value < totalPages.value) currentPage.value++;
        }

        function prevPage() {
            if (currentPage.value > 1) currentPage.value--;
        }

        watch([filteredHistory, pageSize], () => {
            currentPage.value = 1;
        });

        function selectOption(option) {
            selectedOption.value = option;
        }
        async function loadBalance() {
            loadingBalance.value = true;
            if (!await requireAuth()) { loadingBalance.value = false; return }
            balance.value = await getCreditBalance();
            loadingBalance.value = false;
            console.log('balance', balance.value);
            balanceHistory.value = await getCreditTransactions();
        }
        loadBalance();

        async function startCheckout() {
            if (!selectedOption.value) {
                highlightMessage.value = true;
                setTimeout(() => (highlightMessage.value = false), 1200);
                return;
            }
            try {
                const priceId = selectedOption.value.priceId;
                if (!priceId) {
                    infoTitle.value = 'Package Unavailable';
                    infoMessage.value = 'This credit package is not configured for this environment yet.';
                    showInfoPopup.value = true;
                    showInfoPopupCancelButton.value = false;
                    return;
                }
                const response = await postCreditPay(priceId);
                if (!response) return;

                const data = await response.json();
                if (!response.ok || !data.checkout_url) {
                    infoTitle.value = 'Error!';
                    infoMessage.value = 'Error starting checkout. Please try again.';
                    showInfoPopup.value = true;
                    showInfoPopupCancelButton.value = false;
                    return;
                }

                window.location.href = data.checkout_url;
            } catch (err) {
                infoTitle.value = 'Error!';
                infoMessage.value = 'Error starting checkout. Please check your connection and try again.';
                showInfoPopup.value = true;
                showInfoPopupCancelButton.value = false;
            }
        }

        return {
            balance,
            loadingBalance,
            balanceHistory,
            sortedHistory,
            creditOptions,
            selectedOption,
            selectOption,
            startCheckout,
            loadBalance,
            highlightMessage,
            detailTypeMap,
            selectedType,
            selectedDescription,
            selectedDateRange,
            uniqueTypes,
            uniqueDescriptions,
            filteredHistory,
            pageSizeOptions,
            pageSize,
            currentPage,
            totalPages,
            paginatedHistory,
            nextPage,
            prevPage,
            showInfoPopup,
            infoTitle,
            infoMessage,
            showInfoPopupCancelButton
        }
    }
}
</script>
<style scoped>
.main-content {
    margin: 1rem;
    display: flex;
    flex-direction: column;
    gap: 1rem;
}

.credit-info {
    padding: 24px 32px;
    border-bottom: 1px solid var(--border-color);
}

.balance-row {
    margin-top: 1rem;
    width: 100%;
    display: flex;
    justify-content: flex-start;
    align-items: flex-start;
}

.balance-card {
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: center;
    gap: 30px;
    background: linear-gradient(45deg, var(--purple) 0%, var(--purple-light) 50%, var(--purple) 100%);
    padding: 1rem 2rem;
    border-radius: 8px;
    max-width: 600px;
    min-height: 100px;
}

.balance-label {
    color: #fff;
}

.balance {
    font-size: 2rem;
    font-weight: 700;
    color: #fff;
}

.balance-label,
.balance {
    text-shadow: 0 0.5px 2px black;
}

.credit-payment-panel-outer {
    padding: 24px 32px;
    border-bottom: 1px solid var(--border-color);
}


.credit-payment-panel {
    width: 100%;
    display: grid;
    grid-template-columns: 2fr 1fr;
    align-items: stretch;
    gap: 10px
}

.payment-panel {
    border: 1px solid var(--purple);
    background-color: var(--purple-faded);
    background-color: #f9fafb;
    padding: 24px;
    text-align: center;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    height: 100%;
    transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.payment-panel-empty {
    border-color: transparent !important;
    background-color: #f3f4f6 !important;
}

.credit-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
    gap: 20px;
    width: 100%;
    box-sizing: border-box;
}

.credit-option {
    border-radius: 8px;
    border: 2px solid var(--border-color);
    border-radius: 8px;
    padding: 20px;
    text-align: center;
    cursor: pointer;
    transition: transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease;
    position: relative;
    background: #fff;
}

.credit-option:hover {
    transform: translateY(-6px) scale(1.02);
    box-shadow: 0 8px 16px rgba(0, 0, 0, 0.12);
}

.credit-option.selected {
    position: relative;
    border-color: var(--purple);
    box-shadow: none;
    overflow: hidden;
}

.credit-option.selected::before {
    content: "";
    position: absolute;
    left: 0;
    bottom: 0;
    width: 100%;
    height: 100%;
    background: var(--purple-faded);
    animation: riseBlue 0.4s forwards ease-out;
    z-index: 0;
}

.credit-option>* {
    position: relative;
    z-index: 1;
    transition: color 0.4s ease-out;
}

@keyframes riseBlue {
    0% {
        height: 0;
    }

    100% {
        height: 100%;
    }
}


.credit-amount {
    font-size: 24px;
    font-weight: 700;
}

.credit-price {
    font-size: 16px;
    color: var(--text-secondary);
    font-weight: 500;
}


.payment-panel:hover {
    transform: translateY(-4px) scale(1.02);
    box-shadow: 0 6px 14px rgba(0, 0, 0, 0.1);
}

.payment-panel-text {
    font-size: 16px;
    font-weight: 500;
    margin-bottom: 16px;
}

.payment-panel-text span {
    display: block;
    font-size: 20px;
    font-weight: 700;
    color: var(--purple);
    margin-top: 4px;
}


.arrow-hint {
    font-size: 24px;
    display: block;
    color: var(--purple);
    margin-top: 8px;
}

.checkout-btn {
    padding: 12px 24px;
    background: var(--purple);
    color: white;
    border: none;
    border-radius: 4px;
    cursor: pointer;
}

.checkout-btn:disabled {
    background: #94a3b8;
    cursor: not-allowed;
}

@media (max-width: 800px) {
    .main-content {
        flex-direction: column;
    }
}

@media (max-width: 480px) {
    .credit-grid {
        grid-template-columns: 1fr;
    }
}


.payment-panel-text.highlight {
    color: var(--accent-color);
    font-weight: 700;
    animation: pulse 0.6s ease-in-out 2;
}

@keyframes pulse {
    0% {
        transform: scale(1);
    }

    50% {
        transform: scale(1.05);
    }

    100% {
        transform: scale(1);
    }
}

.credit-history {
    padding: 1rem;
}

.credit-history h3 {
    text-align: center;
}

.filter-panel {
    display: flex;
    gap: 3rem;
    justify-content: center;
    margin-top: 1rem;
    flex-wrap: wrap;
}

.filter-panel label {
    display: flex;
    flex-direction: row;
    align-items: center;
    font-size: 14px;
    color: var(--text-secondary);
    gap: 6px;
}

.filter-panel select {
    margin-top: 4px;
    padding: 6px;
    border: 1px solid var(--border-color);
    border-radius: 4px;
}

.history-table {
    width: 100%;
    border-collapse: collapse;
    margin-top: 1rem;
}

.history-table th,
.history-table td {
    text-align: left;
    padding: 1rem;
    border-bottom: 1px solid var(--border-color);
}

.history-table th {
    font-size: 0.85rem;
    text-transform: uppercase;
    color: var(--text-secondary);
    font-weight: 600;
}

.history-table td {
    color: var(--text-primary);
}

.amt.add {
    color: #16a34a;
    font-weight: 600;
}

.amt.beginning {
    color: #16a34a;
    font-weight: 600;
}

.amt.consume {
    color: #dc2626;
    font-weight: 600;
}

.type {
    text-transform: capitalize;
    color: var(--text-secondary);
}

.type.add {
    color: #16a34a;
    font-weight: 600;
}

.type.beginning {
    color: #16a34a;
    font-weight: 600;
}

.type.consume {
    color: #dc2626;
    font-weight: 600;
}

.no-trx {
    text-align: center;
    color: var(--text-secondary);
    margin: 12px 0;
    font-size: 14px;
}

.pagination {
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 1rem;
    margin-top: 1rem;
}

.pagination button {
    padding: 6px 12px;
    border: 1px solid var(--border-color);
    background: var(--background-white);
    border-radius: 4px;
    cursor: pointer;
}

.pagination button:disabled {
    opacity: 0.5;
    cursor: not-allowed;
}
</style>
